import { Fragment, useEffect, useRef, useState } from 'react';
import { Order, OrderMedia, OrderMediaType } from '../../../clients/quipli-service/order/order';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import { toast } from '../../../util/toasts';
import { OrderSection } from '../order-section/order-section';
import { Typography } from '../../../components/typography/typography';
import { Spinner } from '../../../components/spinner/spinner';
import { FileField } from '../../../components/field/field';
import classNames from 'classnames';
import Icon from '../../../components/icons/icon';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { ImageSize } from '../../../clients/quipli-service/image/image';

export const OrderMediaList = ({ order }: { order: Order }) => {
  const { listOrderMedia, getUploadOrderMediaUrl, addOrderMedia } = useOrderApiV1();
  const { uploadToS3 } = useAPI();

  const ref = useRef<CollapsibleRef>(null);

  const [list, setList] = useState<OrderMedia[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const upload = async (files: File[]) => {
    if (files.length === 0 || isUploading) return;

    setIsUploading(true);

    for (const file of files) {
      const type = file.type.match('image/*') ? OrderMediaType.Image : OrderMediaType.Video;

      // if (file.size > 8 * 1024 * 1024) {
      //   toast(`Error uploading ${file.name}: Size must be less than 8MB`, 'error');
      //   continue;
      // }

      try {
        const { url: preSignedUrl, mediaId } = await getUploadOrderMediaUrl(order.id, file.name, type);
        // Save to S3
        await uploadToS3(preSignedUrl, file);
        // Save to DB
        await addOrderMedia(order.id, mediaId, file.name, type);

        toast(`${file.name} uploaded successfully.`);
      } catch (error: any) {
        toast(
          `Error uploading ${file.name}: ${error.message.msg || error.message || error.msg || 'Something went wrong.'}`,
          'error',
        );
      }
    }

    setIsUploading(false);
    fetch();
  };

  const fetch = async () => {
    setIsListLoading(true);

    try {
      setList(await listOrderMedia(order.id));
    } catch (error: any) {
      toast(error.message.msg || error.message, 'error');
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);
  return (
    <>
      <OrderSection>
        <Collapsible
          openAtStart
          ref={ref}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Media
              </Typography>

              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
            </div>
          )}
          afterHiddenContent={
            isUploading ? (
              <Spinner />
            ) : (
              <FileField
                multiple
                className="mt-[8px]"
                accept="image/jpeg, image/png, video/mp4, video/quicktime"
                value={undefined}
                onChange={(files) => upload(files as File[])}
              />
            )
          }
        >
          <div
            className={classNames('flex overflow-x-auto gap-[8px] w-full', {
              'justify-center': isListLoading,
              'mt-[8px] py-[4px]': list.length > 0,
            })}
          >
            {isListLoading ? (
              <Spinner />
            ) : (
              list.map((media, idx) => (
                <Fragment key={`order-media-${idx}`}>
                  <MediaCard media={media} reload={fetch} />
                </Fragment>
              ))
            )}
          </div>
        </Collapsible>
      </OrderSection>
    </>
  );
};

export const MediaCard = ({ media, reload }: { media: OrderMedia; reload: () => void }) => {
  const { deleteOrderMedia, getDownloadOrderMediaUrl } = useOrderApiV1();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const onConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      await deleteOrderMedia(media.orderId, media.id);

      toast('Order media deleted successfully.');
      setIsOpen(false);
      reload();
    } catch (error: any) {
      toast(error.message.msg || error.message || 'Something went wrong.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const onOpen = async (forceDownload: boolean) => {
    try {
      const url = await getDownloadOrderMediaUrl(media.orderId, media.id, forceDownload, ImageSize.Original);
      window.open(url, '_blank');
    } catch (error: any) {
      toast(error.message.msg || error.message || 'Something went wrong.', 'error');
    }
  };
  console.log('MEDIA CARD', media);
  return (
    <>
      <div className="shrink-0 flex flex-col items-center gap-[6px]">
        <div className="w-[140px] h-[140px] rounded-[8px] border border-solid border-grey-200 relative flex items-center justify-center">
          <div className="absolute w-fit right-0 top-0 p-[4px] flex gap-[6px]">
            {/* View */}
            <div
              className="cursor-pointer [&>*]:stroke-grey-400 hover:[&>*]:stroke-grey-700"
              onClick={() => onOpen(false)}
            >
              <Icon icon="LinkExternal" />
            </div>

            {/* Download */}
            <div
              className="cursor-pointer [&>*]:stroke-grey-400 hover:[&>*]:stroke-grey-700"
              onClick={() => onOpen(true)}
            >
              <Icon icon="Download" />
            </div>

            {/* Delete */}
            <div
              className="cursor-pointer [&>*]:stroke-grey-400 hover:[&>*]:stroke-grey-700"
              onClick={() => setIsOpen(true)}
            >
              <Icon icon="Trash" />
            </div>
          </div>

          <div className="mt-[35px]">
            {media.type === 'Image' ? (
              <div className="cursor-pointer" onClick={() => onOpen(false)}>
                <img className="h-[100px] w-[100px] object-contain" src={media.downloadUrl} alt={media.filename} />
              </div>
            ) : (
              <video className="h-[100px] w-[100px]" controls muted>
                <source src={media.downloadUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>

        <Typography type="text-xs" className="text-grey-400">
          {media.uploadedAt.toFormat('ff')}
        </Typography>
      </div>

      <ConfirmModal
        isOpen={isOpen}
        prompt="Delete File"
        headerIcon={
          <div className="flex justify-center">
            <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
          </div>
        }
        additionalText={
          <div className="text-center px-4">
            <Typography type="text-sm" className="text-grey-500">
              Are you sure you want to delete this file?
              <br />
              {`${media.filename} will be permanently deleted.`}
            </Typography>
          </div>
        }
        yesButtonYellow
        yesLabel="Delete"
        noLabel="Cancel"
        onConfirm={() => onConfirm()}
        close={() => setIsOpen(false)}
      />
    </>
  );
};
